import { useTranslation } from "react-i18next";
import "../ModalApp.scss";
import Destroyed from "../../../../assets/images/destroyed-asset.svg";
import { useCallback, useEffect, useMemo, useState } from "react";
import { generateLoadingToast, getDaysDifference, listDateFormat } from "../../../../_utils";
import { useAppDispatch, useAppSelector, useIsLoggedSelector } from "../../../../services/hooks.service";
import { saveDelistedPairChecked, savePresalePairChecked } from "../../../../redux/_actions/persist.action";
import { useMainWallet } from "../../../../wallets/useMainWallet";
import { toast } from "react-toastify";
import classNames from "classnames";
import { IPair } from "../../../../interfaces";
import { ZERO_TIMER } from "../../../../constants/constants";
import { getLogo } from "../../../../wallets/providers";
import { initialUserOrdersArrayState, useGetOrdersQuery, useCancelMultipleOrdersMutation } from "@ultrade/react-sdk";
import { OrderExecution, OrderExecutionType } from "@ultrade/ultrade-js-sdk";
import { checkingTradingKeyError, errorHandler } from "../../../../_utils/errorHandlers";
import { ORDER_LIMIT } from "../../../../constants/constants";

export const MaintenanceWarn = () => {
  const { t } = useTranslation();

  return (
    <p className="warning">
      <img src={Destroyed} alt="icon" className="warning_img"/>
      <span>
        {`${t("error_msg.maintenance_mode")} ${t("error_msg.maintenance_mode_part")}`}
      </span>
    </p>
  );
};

export const DelistingWarn = ({ pair }: { pair: IPair }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isLogged = useIsLoggedSelector();
  const { isMainWallet } = useMainWallet();
  const rtkSdkClient = useAppSelector((state) => state.rtkSdkClient.instance);
  const { data: { open: userOrders } = initialUserOrdersArrayState } = useGetOrdersQuery({ status: OrderExecution.open, symbol: pair.pair_key, limit: ORDER_LIMIT, orderHistoryTab: OrderExecutionType.open }, {
    skip: !isLogged,
    refetchOnMountOrArgChange: true,
  });
  const [cancelMultipleOrders] = useCancelMultipleOrdersMutation();

  // const sdkClient = useAppSelector((state) => state.sdkClient.instance);
  // const userOrders = useAppSelector((state) => state.exchange.userOrders.open);

  const generateText = useMemo(() => {
    const name = `${pair.pair_name?.replace("_", "/")}`;
    const date = listDateFormat(pair.delisting_date, true);
    const numDays = getDaysDifference(pair.delisting_date);
    return t("error_msg.scheduled_for_delisting",{ name, numDays, date });
  }, [pair.id]);

  const handleCancelOrders = useCallback(async () => {
    if (!userOrders.some(o => o.pairId === pair.id)) {
      toast.success(t("success_msg.no_open_orders"), { className: "custom-toast" });
      return;
    }
    const toastId = generateLoadingToast();
    try {
      const orderIds = userOrders.filter(o => o.pairId === pair.id).map(o => o.id);
      if (orderIds.length > 0) {
        await cancelMultipleOrders({ orderIds }).unwrap();
      } else {
        await cancelMultipleOrders({ pairId: pair.id }).unwrap();
      }
      toast.dismiss(toastId);
    } catch (error) {
      console.log("Cancel Order Error :", error);
      errorHandler(error, { toastId });
      checkingTradingKeyError(error, rtkSdkClient);
    }
  }, [userOrders, pair.id, t, cancelMultipleOrders, rtkSdkClient]);

  return (
    <p className={classNames("warning flex_warning", {"hide_warning": false})}>
      <span>
        <img src={Destroyed} alt="icon" className="warning_img"/>
        <span>
          { generateText }
          { pair.new_orders_disabled && t("error_msg.trading_is_disabled") }
          { t("error_msg.cancel_open_orders") }
        </span>
      </span>
      <span>
        <button className="red_btn" onClick={handleCancelOrders} disabled={!isMainWallet}> 
          { t("error_msg.cancel_orders") }
        </button>
        <button onClick={() => dispatch(saveDelistedPairChecked(pair.id))} className="red_btn"> 
          <i className="far fa-times"/>
        </button>
      </span>
    </p>
  );
};

export const SheduledModeWarn = ({ date, hide }) => {
  const { t } = useTranslation();
  const [timer, setTimer] = useState(ZERO_TIMER);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timerId = setInterval(() => {
      const timeDiff = new Date(date).getTime() - new Date().getTime();
      if (timeDiff <= 0) {
        if (!visible) setVisible(true);
        setTimer(ZERO_TIMER);
        clearInterval(timerId);
        return;
      }
      
      const minutes = Math.floor(timeDiff / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
      const formattedMinutes = minutes.toString().padStart(2, "0");
      const formattedSeconds = seconds.toString().padStart(2, "0");
      setTimer(`${formattedMinutes}:${formattedSeconds}`);
      if (!visible) setVisible(true);
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, []);

  return (
    <p className={classNames("warning flex_warning", {"hide_warning": !visible})}>
      <span>
        <img src={Destroyed} alt="icon" className="warning_img"/>
        { timer === ZERO_TIMER
          ? <span>{ t("error_msg.mtnc_notification_3") }</span>
          : <>
            <span style={{fontWeight: 600}}>{ t("obd_modals.platform_maintenance") }:</span>
            <span>{ t("error_msg.mtnc_notification_1") }</span>
            <span className="warning_timer">{ timer }</span>
            <span>{ t("error_msg.mtnc_notification_2") }</span>
          </>
        }
      </span>
      <button onClick={() => hide(false)}> 
        <i className="far fa-times"/>
      </button>
    </p>
  );
}

export const PreSaleWarn = ({ pair }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  return (
    <p className={classNames("warning flex_warning")}>
      <span>
        <span> {pair?.pair_name?.split("_")[0]} </span>
        <img className="chain_icon" src={getLogo(pair?.base_chain_id)} alt="icon" />
        {"/"}
        <span> {pair?.pair_name?.split("_")[1]} </span>
        <img className="chain_icon" src={getLogo(pair?.price_chain_id)} alt="icon" />
        {' '}
        { t('error_msg.is_pre_sale_mode') }
        <span className="second_gray_txt">{ t('error_msg.only_buy_allowed') }</span>
      </span>
      <button onClick={() => dispatch(savePresalePairChecked(pair.id))}> 
        <i className="far fa-times"/>
      </button>
    </p>
  );
}
